"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import { getSelectedWorkCopy } from "@/lib/selected-work-copy";
import { buildProjectHref } from "@/lib/i18n/paths";
import Link from "@/components/ui/CcLink";
import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { CcNavInlineDetailLabel } from "@/components/layout/CcNavInlineDetailLabel";
import { CcMegaAllLink } from "@/components/ui/CcMegaAllLink";
import type {
  NavProjectMegaColumn,
  NavProjectsMegaCopy,
} from "@/lib/cc-nav-projects-mega";
import type { NavProjectItem } from "@/lib/projects";
import {
  useCcNavMegaMountEl,
  useCcNavMegaPanelPosition,
} from "@/lib/cc-nav-mega-panel-position";

const OPEN_DELAY_MS = 100;
const CLOSE_DELAY_MS = 240;
const HOVER_INTENT_MQ =
  "(hover: hover) and (pointer: fine) and (min-width: 811px)";

/** Panel is 2× `min(42rem, 100vw - 48px)`; ×1.05 covers hover scale without upscaling. */
const NAV_PROJECTS_PREVIEW_IMAGE_SIZES =
  "(min-width: 811px) calc((min(42rem, 100vw - 48px) - 2.5rem) / 2 * 1.05), 0px";
const NAV_PROJECTS_PREVIEW_IMAGE_QUALITY = 85;

type CcNavProjectsMenuProps = {
  label: string;
  href: string;
  isActive: boolean;
  projectItems: NavProjectItem[];
  projectMegaColumns: NavProjectMegaColumn[];
  megaCopy: NavProjectsMegaCopy;
  onNavigate: () => void;
  detailTitle?: string | null;
  detailTitleFallback?: string | null;
  detailInitiallySettled?: boolean;
  onDetailRailActiveChange?: (active: boolean) => void;
  megaMountRef?: RefObject<HTMLElement | null>;
};

export function CcNavProjectsMenu({
  label,
  href,
  isActive,
  projectItems,
  projectMegaColumns: _projectMegaColumns = [],
  megaCopy,
  onNavigate,
  detailTitle = null,
  detailTitleFallback = null,
  detailInitiallySettled = false,
  onDetailRailActiveChange,
  megaMountRef,
}: CcNavProjectsMenuProps) {
  const locale = useSiteLocale();
  const selectedWorkCopy = getSelectedWorkCopy(locale);
  const [detailRailActive, setDetailRailActive] = useState(false);
  const showDetailLayout =
    Boolean(detailTitle) ||
    Boolean(detailTitleFallback) ||
    detailRailActive;
  const detailAria =
    detailTitle ?? detailTitleFallback ?? null;

  const handleDetailRailChange = useCallback(
    (active: boolean) => {
      setDetailRailActive(active);
      onDetailRailActiveChange?.(active);
    },
    [onDetailRailActiveChange],
  );
  const pathname = usePathname();
  const activeDetailIndex = projectItems.findIndex(
    (item) => item.href === pathname,
  );
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const focusFirstRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [hoverIntent, setHoverIntent] = useState(false);
  const megaMountEl = useCcNavMegaMountEl(megaMountRef);
  const pillMounted = Boolean(megaMountEl);
  const alignEnd = useCcNavMegaPanelPosition(
    open,
    megaMountEl,
    triggerRef,
    panelRef,
    [projectItems.length],
    true,
  );

  const clearTimers = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearTimers();
    setOpen(true);
  }, [clearTimers]);

  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers]);

  useLayoutEffect(() => {
    const mq = window.matchMedia(HOVER_INTENT_MQ);
    const sync = () => setHoverIntent(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    closeNow();
  }, [pathname, closeNow]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (!open || !focusFirstRef.current) return;
    focusFirstRef.current = false;
    firstItemRef.current?.focus();
  }, [open]);

  const scheduleOpen = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open) return;
    if (openTimerRef.current !== null) return;
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };

  const scheduleClose = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (!open) return;
    if (closeTimerRef.current !== null) return;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };

  const handlePointerEnter = () => {
    if (!hoverIntent) return;
    scheduleOpen();
  };

  const handlePointerLeave = () => {
    if (!hoverIntent) return;
    scheduleClose();
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!hoverIntent) {
        onNavigate();
        return;
      }
      if (open) {
        firstItemRef.current?.focus();
        return;
      }
      focusFirstRef.current = true;
      openNow();
    }

    if (event.key === "Escape" && open) {
      event.preventDefault();
      closeNow();
      triggerRef.current?.focus();
    }
  };

  const handleRootKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || !open) return;
    event.preventDefault();
    closeNow();
    triggerRef.current?.focus();
  };

  const handleRootBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && rootRef.current?.contains(next)) return;
    if (next instanceof Node && panelRef.current?.contains(next)) return;
    closeNow();
  };

  const handlePanelPointerEnter = () => {
    if (!hoverIntent || !pillMounted) return;
    scheduleOpen();
  };

  const handlePanelPointerLeave = () => {
    if (!hoverIntent || !pillMounted) return;
    scheduleClose();
  };

  const handleItemNavigate = () => {
    closeNow();
    onNavigate();
  };

  if (projectItems.length === 0) {
    return (
      <Link
        href={href}
        priority="primary"
        className={`cc-nav__link ${isActive ? "cc-nav__link--active" : ""} ${showDetailLayout ? "cc-nav__link--has-detail" : ""}`}
        aria-label={detailAria ? `${label} / ${detailAria}` : undefined}
        onNavigate={onNavigate}
      >
        <CcNavInlineDetailLabel
          label={label}
          detail={detailTitle}
          detailIndex={activeDetailIndex >= 0 ? activeDetailIndex : null}
          detailFallback={detailTitleFallback}
          detailInitiallySettled={detailInitiallySettled}
          onDetailRailActiveChange={handleDetailRailChange}
        />
      </Link>
    );
  }

  const panel = (
      <div
        ref={panelRef}
        id={panelId}
        className={`cc-nav-mega cc-nav-projects-preview ${pillMounted ? "cc-nav-mega--pill-mounted" : ""} ${open ? "cc-nav-mega--open" : ""}`}
        hidden={!open && !hoverIntent}
        inert={!open}
        aria-hidden={!open}
        role="menu"
        onPointerEnter={handlePanelPointerEnter}
        onPointerLeave={handlePanelPointerLeave}
      >
        <div className="cc-nav-mega__chrome" data-cc-cursor-ignore aria-hidden="true" />
        <ul className="cc-nav-projects-preview__grid">
          {projectItems.map((item, index) => {
            const homeTile = selectedWorkCopy.items.find(
              tile => buildProjectHref(locale, tile.project) === item.href,
            );
            const itemActive =
              pathname === item.href || pathname.startsWith(`${item.href}/`);
            return (
              <li
                key={item.id}
                className="cc-nav-projects-preview__cell"
                role="none"
              >
                <Link
                  ref={index === 0 ? firstItemRef : undefined}
                  href={item.href}
                  className={`cc-nav-projects-preview__card ${itemActive ? "cc-nav-projects-preview__card--active" : ""}`}
                  role="menuitem"
                  onNavigate={handleItemNavigate}
                >
                  <span className="cc-nav-projects-preview__media">
                    <Image
                      src={homeTile?.poster || homeTile?.image || item.imageSrc}
                      alt=""
                      fill
                      sizes={NAV_PROJECTS_PREVIEW_IMAGE_SIZES}
                      quality={NAV_PROJECTS_PREVIEW_IMAGE_QUALITY}
                      loading="lazy"
                      className="cc-nav-projects-preview__img"
                    />
                  </span>
                  <span className="cc-nav-projects-preview__body">
                    <strong className="cc-nav-projects-preview__title">{item.title}</strong>
                    <span className="cc-nav-projects-preview__swap">
                      <span>{item.serviceLabel}</span>
                      <span aria-hidden="true">
                        {selectedWorkCopy.viewCase}
                        <ArrowRight size={12} strokeWidth={2} />
                      </span>
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        <div className="cc-nav-mega__rule" data-cc-cursor-ignore />
        <CcMegaAllLink href={href} onNavigate={handleItemNavigate}>
          {megaCopy.seeAll}
        </CcMegaAllLink>
      </div>
  );

  return (
    <div
      ref={rootRef}
      className={`cc-nav-projects ${open ? "cc-nav-projects--open" : ""} ${!pillMounted && alignEnd ? "cc-nav-projects--align-end" : ""}`}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onKeyDown={handleRootKeyDown}
      onBlur={handleRootBlur}
    >
      <Link
        ref={triggerRef}
        href={href}
        priority="primary"
        className={`cc-nav__link cc-nav-projects__trigger ${isActive ? "cc-nav__link--active" : ""} ${showDetailLayout ? "cc-nav__link--has-detail" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={detailAria ? `${label} / ${detailAria}` : undefined}
        onNavigate={onNavigate}
        onKeyDown={handleTriggerKeyDown}
      >
        <CcNavInlineDetailLabel
          label={label}
          detail={detailTitle}
          detailIndex={activeDetailIndex >= 0 ? activeDetailIndex : null}
          detailFallback={detailTitleFallback}
          detailInitiallySettled={detailInitiallySettled}
          onDetailRailActiveChange={handleDetailRailChange}
        />
      </Link>

      {open ? (
        <span className="cc-nav-projects__bridge" aria-hidden="true" />
      ) : null}

      {pillMounted && megaMountEl
        ? createPortal(panel, megaMountEl)
        : panel}
    </div>
  );
}
